import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { STRIPE_CONFIG, hasUnlimitedAccess } from '../config/stripe';

interface SubscriptionManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

const SubscriptionManager: React.FC<SubscriptionManagerProps> = ({
  isOpen,
  onClose,
}) => {
  const { user, userProfile, updateSubscription } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen || !user || !userProfile) return null;

  const isPremium = hasUnlimitedAccess(userProfile.subscriptionTier);
  const currentPlan = isPremium ? STRIPE_CONFIG.PLANS.PREMIUM : STRIPE_CONFIG.PLANS.FREE;

  const handleUpgrade = () => {
    // client_reference_id is how the Stripe webhook knows WHICH account paid
    // (without it a payment could never be linked and the user stayed free).
    const params = new URLSearchParams({ client_reference_id: user!.uid });
    if (user?.email) params.set('prefilled_email', user.email);
    window.open(`https://buy.stripe.com/6oU8wP59t0Tz1gE3Sr4Vy01?${params.toString()}`, '_blank');
  };

  const handleDowngrade = async () => {
    if (window.confirm('Are you sure you want to downgrade to the Free plan? You will lose unlimited access and be limited to 25 AI processing and 3 manual input sessions (30 minutes each) per month.')) {
      setIsLoading(true);
      try {
        // Cancel in Stripe first: a Firestore-only downgrade left the card being
        // charged every month. Stripe stops renewal at the end of the paid
        // period and the webhook then moves the account to free.
        const token = await user!.getIdToken();
        const resp = await fetch(`${process.env.REACT_APP_FUNCTIONS_URL || 'https://us-central1-qme-ratings-v2.cloudfunctions.net'}/cancelMySubscription`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.ok) {
          const { endsAt } = await resp.json();
          const when = endsAt ? new Date(endsAt * 1000).toLocaleDateString() : 'the end of the billing period';
          alert(`Your subscription is canceled. You keep Premium until ${when}; you will not be charged again.`);
        } else if (resp.status === 409) {
          // No Stripe subscription (Premium was granted by an admin): just switch.
          await updateSubscription('free');
          alert('Switched to the Free plan.');
        } else {
          throw new Error(`cancel failed: ${resp.status}`);
        }
      } catch (error) {
        console.error('Downgrade error:', error);
        alert('Failed to downgrade subscription. Please try again.');
      }
      setIsLoading(false);
    }
  };



  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="card p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-slide-up">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-content">Subscription Management</h2>
            <button
              onClick={onClose}
              className="text-content-tertiary hover:text-content text-2xl transition-colors"
            >
              ×
            </button>
          </div>

          {/* Current Plan Status */}
          <div className="mb-6">
            <div className={`p-4 rounded-lg border-2 ${
              isPremium
                ? 'bg-accent-900/20 border-accent-500'
                : 'bg-surface-overlay border-border'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xl font-semibold text-content">
                  Current Plan: {currentPlan.name}
                </h3>
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isPremium
                    ? 'bg-accent-600 text-white'
                    : 'bg-surface-overlay text-content-secondary'
                }`}>
                  {isPremium ? '✨ Premium' : '🆓 Free'}
                </div>
              </div>
              
              <div className="text-content-secondary mb-3">
                <strong>${currentPlan.price}/month</strong>
              </div>

              <ul className="space-y-2">
                {currentPlan.features.map((feature, index) => (
                  <li key={index} className="flex items-center text-content-secondary">
                    <span className="text-green-400 mr-2">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Usage Statistics */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-content mb-3">Current Usage</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-surface-overlay p-4 rounded-lg border border-border">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-content-secondary">AI Processing</span>
                  <span className="text-content font-medium">
                    {userProfile.usage.aiProcessingCount}/{isPremium ? '∞' : '25'}
                  </span>
                </div>
                {!isPremium && (
                  <div className="w-full bg-surface-sunken rounded-full h-2">
                    <div 
                      className="bg-accent-500 h-2 rounded-full"
                      style={{ 
                        width: `${Math.min((userProfile.usage.aiProcessingCount / 25) * 100, 100)}%` 
                      }}
                    ></div>
                  </div>
                )}
              </div>

              <div className="bg-surface-overlay p-4 rounded-lg border border-border">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-content-secondary">Manual Input Sessions</span>
                  <span className="text-content font-medium">
                    {userProfile.usage.manualSessionCount}/{isPremium ? '∞' : '3'}
                  </span>
                </div>
                {!isPremium && (
                  <div className="w-full bg-surface-sunken rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full"
                      style={{ 
                        width: `${Math.min((userProfile.usage.manualSessionCount / 3) * 100, 100)}%` 
                      }}
                    ></div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Account Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-content mb-3">Account Information</h3>
            <div className="bg-surface-overlay p-4 rounded-lg border border-border space-y-2">
              <div className="flex justify-between">
                <span className="text-content-secondary">Email:</span>
                <span className="text-content">{user.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-content-secondary">User ID:</span>
                <span className="text-content font-mono text-sm">{user.uid.substring(0, 8)}...</span>
              </div>
              <div className="flex justify-between">
                <span className="text-content-secondary">Member Since:</span>
                <span className="text-content">
                  {new Date(userProfile.createdAt).toLocaleDateString()}
                </span>
              </div>
              {userProfile.stripeCustomerId && (
                <div className="flex justify-between">
                  <span className="text-content-secondary">Stripe Customer:</span>
                  <span className="text-content font-mono text-sm">
                    {userProfile.stripeCustomerId.substring(0, 12)}...
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Plan Management Actions */}
          <div className="space-y-3">
            {!isPremium ? (
              <>
                <button
                  onClick={handleUpgrade}
                  className="w-full btn-primary py-3 font-semibold"
                >
                  Upgrade to Premium - ${STRIPE_CONFIG.PLANS.PREMIUM.price}/month
                </button>
                <p className="text-center text-sm text-content-secondary"><a className="underline" href="/terms.html" target="_blank" rel="noreferrer">Terms</a> and <a className="underline" href="/privacy.html" target="_blank" rel="noreferrer">Privacy Policy</a></p>
              </>
            ) : (
              <>
                <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 text-center">
                  <div className="text-green-400 font-semibold mb-1">Premium Active</div>
                  <div className="text-content-secondary text-sm">
                    You have unlimited access to all QME Ratings features
                  </div>
                </div>
                
                <button
                  onClick={handleDowngrade}
                  disabled={isLoading}
                  className="w-full btn-danger disabled:opacity-50"
                >
                  {isLoading ? 'Processing...' : 'Downgrade to Free Plan'}
                </button>
              </>
            )}

            <button
              onClick={onClose}
              className="w-full btn-secondary"
            >
              Close
            </button>
          </div>
        </div>
      </div>

    </>
  );
};

export default SubscriptionManager;
